/**
 * surfaceContext — the header chip for the default page floor.
 *
 * The floor's dock is "about" whatever page it sits on, so its chip is the
 * destination the path belongs to (label + icon from the destinations catalog).
 * A path no destination covers still gets a chip, a generic one, so the header
 * never reads as empty on the floor.
 */
import { destinationForPath } from '@shared/navigation/destinations';

import type { SurfaceContext } from './chatDockTypes';

/** The chip for a page the catalog does not know. */
const UNKNOWN_PAGE_CONTEXT: SurfaceContext = { label: 'This page', icon: 'Sparkles' };

/** A journal entry route: `/journal/<date>`; the date segment is the entry's name. */
const JOURNAL_ENTRY = /^\/journal\/([^/?#]+)/;

/**
 * `base` with `title` attached when it is non-blank; `base` itself otherwise,
 * so a memoised descriptor keeps referential identity while a title loads.
 */
export function titledContext(
  base: SurfaceContext,
  title: string | null | undefined
): SurfaceContext {
  const trimmed = title?.trim();
  return trimmed ? { ...base, title: trimmed } : base;
}

/**
 * The context chip for the page floor at `pathname`: the matching destination's
 * label and icon, or a generic "This page" when no destination covers the path.
 * A journal entry also carries its date as the title.
 */
export function pageSurfaceContext(pathname: string): SurfaceContext {
  const destination = destinationForPath(pathname);
  if (!destination) return UNKNOWN_PAGE_CONTEXT;
  const base: SurfaceContext = { label: destination.label, icon: destination.icon };
  const entry = destination.id === 'journal' ? JOURNAL_ENTRY.exec(pathname) : null;
  return titledContext(base, entry?.[1]);
}
